import request from '@/utils/request'

/**
 * 字典类型常量
 * @type {Object}
 */
export const DICT_TYPES = {
  AREA_TYPE: 'AREA_TYPE',        // 区域类型
  BRIDGE_TYPE: 'BRIDGE_TYPE',    // 桥梁类型
  VESSEL_TYPE: 'VESSEL_TYPE'     // 船舶类型
}

/**
 * 获取所有数据字典项（基于用户权限）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     [key: string]: Array<{
 *       id: number,
 *       dictType: string,
 *       dictCode: string,
 *       dictValue: string,
 *       dictDesc: string,
 *       isEnabled: number,
 *       isEditable: boolean,
 *       isDeleted: boolean,
 *       isJson: boolean,
 *       jsonValue: string,
 *       sortOrder: number,
 *       region: number,
 *       userId: number,
 *       createTime: string,
 *       updateTime: string
 *     }>
 *   }
 * }>} 按类型分组的字典项列表
 */
export function getAllDictsByUserScope() {
  return request({
    url: '/dict/user/all',
    method: 'get'
  })
}

/**
 * 获取指定类型的数据字典列表（基于用户权限）
 * @param {string} dictType 字典类型，如：AREA_TYPE、BRIDGE_TYPE
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     dictType: string,
 *     dictCode: string,
 *     dictValue: string,
 *     dictDesc: string,
 *     isEnabled: number,
 *     isEditable: boolean,
 *     isDeleted: boolean,
 *     isJson: boolean,
 *     jsonValue: string,
 *     sortOrder: number,
 *     region: number,
 *     userId: number,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 字典项列表
 */
export function getDictsByTypeAndUserScope(dictType) {
  return request({
    url: `/dict/user/type/${dictType}`,
    method: 'get'
  })
}

/**
 * 获取指定类型的数据字典Map（基于用户权限）
 * @param {string} dictType 字典类型，如：AREA_TYPE、BRIDGE_TYPE
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     [key: string]: string
 *   }
 * }>} 字典编码到字典值的映射
 */
export function getDictMapByTypeAndUserScope(dictType) {
  return request({
    url: `/dict/user/map/${dictType}`,
    method: 'get'
  })
}

/**
 * 获取所有数据字典项
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     [key: string]: Array<{
 *       id: number,
 *       dictType: string,
 *       dictCode: string,
 *       dictValue: string,
 *       dictDesc: string,
 *       isEnabled: number,
 *       isEditable: boolean,
 *       isDeleted: boolean,
 *       isJson: boolean,
 *       jsonValue: string,
 *       sortOrder: number,
 *       region: number,
 *       userId: number,
 *       createTime: string,
 *       updateTime: string
 *     }>
 *   }
 * }>} 按类型分组的字典项列表
 */
export function getAllDicts() {
  return request({
    url: '/dict/all',
    method: 'get'
  })
}

/**
 * 获取所有字典类型
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<string>
 * }>} 字典类型列表
 */
export function getAllDictTypes() {
  return request({
    url: '/dict/types',
    method: 'get'
  })
}

/**
 * 获取指定类型的数据字典列表
 * @param {string} dictType 字典类型，如：AREA_TYPE、BRIDGE_TYPE、ALERT_LEVEL
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     dictType: string,
 *     dictCode: string,
 *     dictValue: string,
 *     dictDesc: string,
 *     isEnabled: number,
 *     isEditable: boolean,
 *     isDeleted: boolean,
 *     isJson: boolean,
 *     jsonValue: string,
 *     sortOrder: number,
 *     region: number,
 *     userId: number,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 字典项列表
 */
export function getDictsByType(dictType) {
  return request({
    url: `/dict/type/${dictType}`,
    method: 'get'
  })
}

/**
 * 获取指定类型的数据字典Map
 * @param {string} dictType 字典类型，如：AREA_TYPE、BRIDGE_TYPE、ALERT_LEVEL
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     [key: string]: string
 *   }
 * }>} 字典编码到字典值的映射
 */
export function getDictMapByType(dictType) {
  return request({
    url: `/dict/map/${dictType}`,
    method: 'get'
  })
}

/**
 * 根据ID获取数据字典项
 * @param {number} id 字典项ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     dictType: string,
 *     dictCode: string,
 *     dictValue: string,
 *     dictDesc: string,
 *     isEnabled: number,
 *     isEditable: boolean,
 *     isDeleted: boolean,
 *     isJson: boolean,
 *     jsonValue: string,
 *     sortOrder: number,
 *     region: number,
 *     userId: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 字典项详情
 */
export function getDictById(id) {
  return request({
    url: `/dict/${id}`,
    method: 'get'
  })
}

/**
 * 创建数据字典项
 * @param {Object} data 字典项数据
 * @param {string} data.dictType 字典类型
 * @param {string} data.dictCode 字典编码
 * @param {string} data.dictValue 字典值
 * @param {string} [data.dictDesc] 字典描述
 * @param {number} [data.isEnabled=1] 是否启用：1-启用，0-禁用
 * @param {boolean} [data.isJson=false] 是否为JSON格式值
 * @param {string} [data.jsonValue] JSON格式的值
 * @param {number} [data.sortOrder] 排序序号
 * @param {number} [data.region] 适用范围
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     dictType: string,
 *     dictCode: string,
 *     dictValue: string,
 *     dictDesc: string,
 *     isEnabled: number,
 *     isEditable: boolean,
 *     isDeleted: boolean,
 *     isJson: boolean,
 *     jsonValue: string,
 *     sortOrder: number,
 *     region: number,
 *     userId: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 创建的字典项
 */
export function createDict(data) {
  return request({
    url: '/dict',
    method: 'post',
    data
  })
}

/**
 * 更新数据字典项
 * @param {number} id 字典项ID
 * @param {Object} data 字典项数据
 * @param {string} data.dictType 字典类型
 * @param {string} data.dictCode 字典编码
 * @param {string} data.dictValue 字典值
 * @param {string} [data.dictDesc] 字典描述
 * @param {number} [data.isEnabled] 是否启用：1-启用，0-禁用
 * @param {boolean} [data.isJson] 是否为JSON格式值
 * @param {string} [data.jsonValue] JSON格式的值
 * @param {number} [data.sortOrder] 排序序号
 * @param {number} [data.region] 适用范围
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     dictType: string,
 *     dictCode: string,
 *     dictValue: string,
 *     dictDesc: string,
 *     isEnabled: number,
 *     isEditable: boolean,
 *     isDeleted: boolean,
 *     isJson: boolean,
 *     jsonValue: string,
 *     sortOrder: number,
 *     region: number,
 *     userId: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 更新后的字典项
 */
export function updateDict(id, data) {
  return request({
    url: `/dict/${id}`,
    method: 'put',
    data
  })
}

/**
 * 删除数据字典项
 * @param {number} id 字典项ID
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 删除结果
 */
export function deleteDict(id) {
  return request({
    url: `/dict/${id}`,
    method: 'delete'
  })
}

/**
 * 获取告警级别值
 * @param {string} dictCode 告警级别编码
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: number
 * }>} 告警级别对应的数值
 */
export function getAlertLevelValue(dictCode) {
  return request({
    url: `/dict/alert-level/${dictCode}`,
    method: 'get'
  })
} 